// @ts-strict-ignore
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { createSession, faro } from '@grafana/faro-web-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, distinctUntilChanged, exhaustMap, filter, map, mergeMap, of } from 'rxjs';
import { UsersService } from '../../api/authorisation/services/users.service';
import { filterDefined } from '../../functions/rxjs-pipes/filter-defined';
import { EditorTreeService } from '../../services/editor-tree-service/editor-tree.service';
import { WINDOW } from '../../services/window.service';
import { selectBoard } from '../board/board.selectors';
import { inventoriesEffectsActions } from '../inventories/inventories.actions';
import { selectActiveBoardMigrationStatus } from '../migration-status/migration-status.selectors';
import { routerEffectsActions } from './router.actions';
import { CustomRouterNavigationAction } from './router.types';

@Injectable()
export class RouterEffects {
  patchActiveBoardAndTenant$ = createEffect(
    () =>
      combineLatest([
        this._actions$.pipe(ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION)),
        this._store.select(selectActiveBoardMigrationStatus),
      ]).pipe(
        map(([navigationData, migrationStatus]) => ({
          activeBoard: navigationData.payload.routerState.params.boardId,
          activeTenant: navigationData.payload.routerState.params.organisationId,
          migrationStatus,
        })),
        filter(
          ({ activeBoard, activeTenant, migrationStatus }) =>
            !!activeBoard &&
            !!activeTenant &&
            activeBoard === migrationStatus?.boardId &&
            !!migrationStatus.migrationStatus
        ),
        distinctUntilChanged((prev, curr) => prev.activeBoard === curr.activeBoard),
        mergeMap(({ activeBoard, activeTenant }) => {
          return this._usersService.setActiveState({ body: { activeBoard, activeTenant } }).pipe(
            catchError((err) => {
              if (err instanceof HttpErrorResponse && err.status === 404) {
                console.warn("We're trying to set active state to a non existant board");
                return of();
              }
              return of(err);
            })
          );
        })
      ),
    { dispatch: false }
  );

  routeBoardIdChanged$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION),
      map((navigationData) => navigationData.payload.routerState.params),
      filter(({ boardId, organisationId }) => !!boardId && !!organisationId),
      distinctUntilChanged((prev, curr) => prev.boardId === curr.boardId),
      exhaustMap(({ boardId, organisationId }) => {
        if (faro.api !== undefined) {
          faro.api.setSession(
            createSession({
              board_id: boardId,
              tenant_id: organisationId,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              language: this._window.navigator.language,
            })
          );
        }
        return of(
          routerEffectsActions.routeBoardIdChanged({ boardId }),
          inventoriesEffectsActions.resetInventories() // reset inventories when board changes
          // TODO: We should reset all our stores except user me when the board changes
        );
      })
    )
  );

  routeTenantIdChanged$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION),
      map((navigationData) => navigationData.payload.routerState.params.organisationId),
      filterDefined,
      distinctUntilChanged(),
      map((tenantId) => routerEffectsActions.routeTenantIdChanged({ tenantId }))
    )
  );

  /**
   * Sets the view parameters of the editor tree service
   */
  setEditorTreeServiceViewParameters$ = createEffect(() =>
    combineLatest([
      this._actions$.pipe(ofType<CustomRouterNavigationAction>(ROUTER_NAVIGATION)),
      this._store.select(selectBoard),
    ]).pipe(
      filter(([navigationData, board]) => {
        const url = navigationData.payload.routerState.url;
        const boardId = navigationData.payload.routerState.params.boardId;
        return !!url && !!board && !!boardId && board.id === boardId;
      }),
      map(([navigationData, board]) => ({ url: navigationData.payload.routerState.url, board })),
      exhaustMap(({ url, board }) => {
        this._editorTreeService.setViewParameters(board, url);
        return of(routerEffectsActions.editorTreeViewParametersSet());
      })
    )
  );

  constructor(
    private _actions$: Actions,
    private _editorTreeService: EditorTreeService,
    private _store: Store,
    private _usersService: UsersService,
    @Inject(WINDOW) private _window: Window
  ) {}
}
